/**
 * Local UI-preference storage.
 *
 * Only *display* preferences (language, theme, font size) are ever written to localStorage. Authentication uses the
 * HttpOnly `tamreer_session` cookie; no token, e-mail, or other account data is persisted in the browser. Keys are
 * allow-listed so nothing else can be written here by accident, and every access is guarded because Safari private
 * mode / locked-down browsers throw on localStorage access.
 */
import { debugWarn } from "@/lib/debug";

export const PREF_KEYS = Object.freeze({
  lang: "tamreer_lang",
  theme: "tamreer_theme",
  fontSize: "tamreer_font_size",
  hideAmounts: "tamreer_hide_amounts", // Phase 48: "hide amounts" privacy toggle — per device, never sent to the account
  figuresPinned: "tamreer_figures_pinned", // Phase 52: keep the header figures (counts / values) expanded on this device
});

const ALLOWED = new Set(Object.values(PREF_KEYS));

function storage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Read a UI preference; returns `fallback` when unset, unavailable, or the key is not an allowed preference. */
export function readPref(key, fallback = null) {
  if (!ALLOWED.has(key)) return fallback;
  try {
    const v = storage()?.getItem(key);
    return v === null || v === undefined ? fallback : v;
  } catch {
    return fallback;
  }
}

/** Persist a UI preference. Silently no-ops for non-preference keys or when storage is unavailable. */
export function writePref(key, value) {
  if (!ALLOWED.has(key)) return;
  try {
    storage()?.setItem(key, String(value));
  } catch (e) {
    // storage unavailable (private mode / quota) — preference stays in memory for this session
    debugWarn(`localStorage write skipped for ${key}`, e);
  }
}
